import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { resendVerifyEmail } from '../api/users';
import AuthLayout from '../components/AuthLayout';
import AppButton from '../components/common/AppButton/AppButton';
import { auth } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

const RESEND_COOLDOWN_SECONDS = 60;

const PendingVerificationPage = () => {
  const { user, logout, retryAuth } = useAuth();
  const navigate = useNavigate();
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const startCooldown = () => {
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    const interval = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    try {
      await retryAuth();
      if (auth.currentUser?.emailVerified) {
        toast.success('Email verified!');
        navigate('/', { replace: true });
      } else {
        toast.error('Email not verified yet. Please check your inbox.');
      }
    } catch {
      toast.error('Failed to check verification status. Please try again.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleResendEmail = async () => {
    if (!auth.currentUser || !auth.currentUser.email || cooldownSeconds > 0) {
      toast.error('Failed to send email. Please try again.');
      return;
    }

    setResending(true);
    try {
      await resendVerifyEmail({ email: auth.currentUser.email });
      toast.success('Verification email sent!');
      startCooldown();
    } catch {
      toast.error('Failed to send email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
  };

  return (
    <AuthLayout title="Verify your email">
      <div className="flex flex-col gap-6 md:gap-8">
        <div className="flex flex-col gap-4">
          <p className="text-dark">
            We&apos;ve sent an email with a verification link to:
          </p>
          <p className="font-semibold text-dark text-center">{user?.email}</p>
          <p className="text-light text-sm">
            Note: Depending on your email provider, it may go to your spam
            folder, so check there before re-sending!
          </p>
          <p className="text-dark">
            Once you have verified your account with this link, then you&apos;ll
            be able to proceed.
          </p>
        </div>

        <div className="flex flex-col gap-6 md:gap-8 px-8">
          <AppButton
            fullWidth
            loading={checkingStatus}
            onClick={handleCheckStatus}
          >
            CHECK VERIFICATION STATUS
          </AppButton>

          <AppButton
            fullWidth
            variant="secondary"
            loading={resending}
            disabled={resending || cooldownSeconds > 0}
            onClick={handleResendEmail}
          >
            {cooldownSeconds > 0
              ? `RE-SEND IN ${cooldownSeconds}s`
              : 'RE-SEND EMAIL'}
          </AppButton>
        </div>

        <div className="bg-warning/20 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className=" text-warning flex-shrink-0" />
          <p>
            Having issues with verification? Please contact us at{' '}
            <a
              href="mailto:support@getpoolguy.com"
              className="text-primary underline"
            >
              support@getpoolguy.com
            </a>
          </p>
        </div>

        <div className="px-8">
          <AppButton fullWidth variant="tertiary" onClick={handleSignOut}>
            LOG OUT
          </AppButton>
        </div>
      </div>
    </AuthLayout>
  );
};

export default PendingVerificationPage;
